import React, { useState } from 'react';
import {
  Modal,
  Stack,
  TextField,
  PrimaryButton,
  DefaultButton,
  MessageBar,
  MessageBarType,
  Text,
  IconButton,
  Spinner,
} from '@fluentui/react';
import { centeredModalStyles, modalContentStyles } from '../styles/dialogs';
import { kreditorenApi } from '../services/api';
import { useAuth } from '../context/AuthContext';

interface IKreditorAnlegenDialogProps {
  isOpen: boolean;
  onDismiss: () => void;
  onSuccess: (kreditor: any) => void;
  initialData?: {
    Kreditorname?: string;
    Kreditorennummer?: string;
  };
}

export const KreditorAnlegenDialog: React.FC<IKreditorAnlegenDialogProps> = ({
  isOpen,
  onDismiss,
  onSuccess,
  initialData,
}) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    Kreditorname: initialData?.Kreditorname || '',
    Kreditorennummer: initialData?.Kreditorennummer || '',
    Kontaktperson: '',
    Email: '',
    Telefonnummer: '',
    Adresse: '',
  });
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    // Validierung
    if (!formData.Kreditorname) {
      setError('Kreditorname ist erforderlich');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      // Erstelle neuen Kreditor
      const newKreditor = await kreditorenApi.create({
        ...formData,
        AuthorEmail: user?.email,
        AuthorName: user?.displayName,
      });

      onSuccess(newKreditor);
      onDismiss();
    } catch (err: any) {
      setError(err.message || 'Fehler beim Anlegen des Kreditors');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onDismiss={onDismiss}
      isBlocking={true}
      styles={centeredModalStyles}
    >
      {/* Header */}
      <div style={modalContentStyles.header}>
        <Text variant="xLarge" styles={{ root: { fontWeight: 600 } }}>
          Neuen Kreditor anlegen
        </Text>
        <IconButton
          iconProps={{ iconName: 'Cancel' }}
          ariaLabel="Schließen"
          onClick={onDismiss}
          disabled={saving}
        />
      </div>

      {/* Body */}
      <div style={modalContentStyles.body}>
        <Stack tokens={{ childrenGap: 16 }}>
          {error && (
            <MessageBar messageBarType={MessageBarType.error} onDismiss={() => setError(null)}>
              {error}
            </MessageBar>
          )}

          <TextField
            label="Kreditorname"
            placeholder="Name des Kreditors"
            value={formData.Kreditorname}
            onChange={(_, value) =>
              setFormData((prev) => ({ ...prev, Kreditorname: value || '' }))
            }
            required
            disabled={saving}
          />

          <TextField
            label="Kreditorennummer"
            placeholder="Nummer des Kreditors (optional)"
            value={formData.Kreditorennummer}
            onChange={(_, value) =>
              setFormData((prev) => ({ ...prev, Kreditorennummer: value || '' }))
            }
            disabled={saving}
          />

          <TextField
            label="Kontaktperson"
            placeholder="Name der Kontaktperson"
            value={formData.Kontaktperson}
            onChange={(_, value) =>
              setFormData((prev) => ({ ...prev, Kontaktperson: value || '' }))
            }
            disabled={saving}
          />

          <TextField
            label="E-Mail"
            placeholder="kontakt@kreditor.de"
            value={formData.Email}
            onChange={(_, value) =>
              setFormData((prev) => ({ ...prev, Email: value || '' }))
            }
            disabled={saving}
          />

          <TextField
            label="Telefonnummer"
            placeholder="+49 123 456789"
            value={formData.Telefonnummer}
            onChange={(_, value) =>
              setFormData((prev) => ({ ...prev, Telefonnummer: value || '' }))
            }
            disabled={saving}
          />

          <TextField
            label="Adresse"
            placeholder="Straße, PLZ Ort"
            value={formData.Adresse}
            onChange={(_, value) =>
              setFormData((prev) => ({ ...prev, Adresse: value || '' }))
            }
            disabled={saving}
            multiline
            rows={3}
          />

          {saving && (
            <Stack horizontalAlign="center" tokens={{ padding: 20 }}>
              <Spinner label="Lege Kreditor an..." />
            </Stack>
          )}
        </Stack>
      </div>

      {/* Footer */}
      <div style={modalContentStyles.footer}>
        <DefaultButton text="Abbrechen" onClick={onDismiss} disabled={saving} />
        <PrimaryButton text="Anlegen" onClick={handleSave} disabled={saving} />
      </div>
    </Modal>
  );
};
